import type { ReportFilter } from '../models/filter.types'
import type { ReportRow } from '../models/report.types'

import { foldSearchText } from '@/shared/lib/string'

function normalized(value: unknown): string {
  return foldSearchText(value)
}

function listOf(value: unknown): string[] {
  const values = Array.isArray(value) ? value : String(value ?? '').split(',')
  return values.map((item) => normalized(item).trim())
}

function numberOf(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const result = Number(value)
  return Number.isFinite(result) ? result : null
}

function matchesMembership(value: unknown, filterValue: unknown): boolean {
  const selected = new Set(listOf(filterValue))
  const values = Array.isArray(value) ? value : [value]
  return values.some((item) => selected.has(normalized(item).trim()))
}

function matchesBetween(value: unknown, filterValue: unknown): boolean {
  const range = Array.isArray(filterValue) ? filterValue : String(filterValue ?? '').split(',')
  const start = numberOf(range[0])
  const end = numberOf(range[1])
  if (start === null || end === null) return false
  const current = numberOf(value)
  return current !== null && current >= Math.min(start, end) && current <= Math.max(start, end)
}

function isEmptyValue(value: unknown): boolean {
  return (
    value === null || value === undefined || value === '' || (Array.isArray(value) && !value.length)
  )
}

function textValues(value: unknown): string[] {
  return (Array.isArray(value) ? value : [value]).map(normalized)
}

function matchesRegex(value: unknown, filterValue: unknown): boolean | null {
  try {
    return new RegExp(String(filterValue ?? ''), 'iu').test(String(value ?? ''))
  } catch {
    // An invalid persisted expression must not accidentally admit every row.
    return null
  }
}

function matchesFilter(row: ReportRow, filter: ReportFilter): boolean {
  const value = row[filter.field]
  const texts = textValues(value)
  const query = normalized(filter.value)

  switch (filter.match_mode) {
    case 'starts_with':
      return texts.some((text) => text.startsWith(query))
    case 'contains':
      return texts.some((text) => text.includes(query))
    case 'not_contains':
      return texts.every((text) => !text.includes(query))
    case 'ends_with':
      return texts.some((text) => text.endsWith(query))
    case 'equals':
      return texts.some((text) => text === query)
    case 'not_equals':
      return texts.every((text) => text !== query)
    case 'in':
      return matchesMembership(value, filter.value)
    case 'not_in':
      return !matchesMembership(value, filter.value)
    case 'between':
      return matchesBetween(value, filter.value)
    case 'greater_than': {
      const current = numberOf(value)
      const expected = numberOf(filter.value)
      return current !== null && expected !== null && current > expected
    }
    case 'greater_than_or_equal_to': {
      const current = numberOf(value)
      const expected = numberOf(filter.value)
      return current !== null && expected !== null && current >= expected
    }
    case 'less_than': {
      const current = numberOf(value)
      const expected = numberOf(filter.value)
      return current !== null && expected !== null && current < expected
    }
    case 'less_than_or_equal_to': {
      const current = numberOf(value)
      const expected = numberOf(filter.value)
      return current !== null && expected !== null && current <= expected
    }
    case 'regex':
      return matchesRegex(value, filter.value) === true
    case 'not_regex': {
      const matched = matchesRegex(value, filter.value)
      return matched === null ? false : !matched
    }
    case 'is_empty':
      return isEmptyValue(value)
    case 'is_not_empty':
      return !isEmptyValue(value)
  }
}

/** Apply the contract match-mode vocabulary without mutating report rows. */
export function filterRows(
  rows: readonly ReportRow[],
  filters: readonly ReportFilter[],
): ReportRow[] {
  if (filters.length === 0) return [...rows]
  return rows.filter((row) => filters.every((filter) => matchesFilter(row, filter)))
}
